/**
 * Converts stored rich-text form titles to the plain text that title-length validation measures.
 * The caller supplies the runtime's entity decoder so this stays dependency-free and can run in
 * both the API and the browser bundle.
 */
export function stripHtml(html, decodeHtmlEntities) {
	if (!html) return "";
	const decodedHtml = decodeHtmlEntities(html);
	// LSP-17717: a linear scan, not /<[^>]*>/g. The regex backtracks quadratically on input made of
	// unmatched "<" openers (a 1 MB run of "<" takes minutes), and a form headline reaches this from
	// POST /forms and from contact-admin's own title counter, so the pathological input is caller-supplied.
	let plainText = "";
	let cursor = 0;

	while (cursor < decodedHtml.length) {
		const tagStart = decodedHtml.indexOf("<", cursor);
		if (tagStart === -1) {
			plainText += decodedHtml.slice(cursor);
			break;
		}

		const tagEnd = decodedHtml.indexOf(">", tagStart + 1);
		if (tagEnd === -1) {
			// No closing ">" anywhere after it: the rest is literal text, "<" included.
			plainText += decodedHtml.slice(cursor);
			break;
		}

		plainText += decodedHtml.slice(cursor, tagStart);
		cursor = tagEnd + 1;
	}

	return plainText.trim();
}

/** Maximum value accepted by database-backed public-form text columns. */
export const FORM_FIELD_MAX_LENGTH = 255;

/** Default MaxLength injected for a public-form field that has no explicit configured limit. */
export const PUBLIC_FORM_DEFAULT_MAX_LENGTH = 250;

/** Practical SMTP mailbox ceiling for the ASCII-only address contract accepted by Postmark SMTP. */
export const EMAIL_MAX_LENGTH = 254;

/** RFC 5321 local-part ceiling, in ASCII characters for the supported Postmark contract. */
export const EMAIL_LOCAL_PART_MAX_LENGTH = 64;

/** Whether a value fits the supported email-address length ceiling. */
export function isEmailWithinMaxLength(value) {
	return typeof value === "string" && value.length <= EMAIL_MAX_LENGTH;
}

/**
 * Validates the ASCII-only address subset documented by Postmark and the DNS label rules required
 * for an externally deliverable domain. This intentionally does not perform DNS or mailbox checks.
 */
export function isPostmarkCompatibleEmail(value) {
	if (!isEmailWithinMaxLength(value) || !/^[\x00-\x7F]+$/.test(value)) return false;

	const atIndex = value.indexOf("@");
	if (atIndex < 1 || atIndex !== value.lastIndexOf("@")) return false;

	const localPart = value.slice(0, atIndex);
	const domain = value.slice(atIndex + 1);
	if (localPart.length > EMAIL_LOCAL_PART_MAX_LENGTH || !/^[A-Za-z0-9.!#$%&'`*+\-/=?^_{}|~]+$/.test(localPart)) return false;
	if (localPart.startsWith(".") || localPart.endsWith(".") || localPart.includes("..")) return false;

	const domainLabels = domain.split(".");
	if (domainLabels.length < 2) return false;
	return domainLabels.every(
		(label) => label.length <= 63 && /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?$/.test(label)
	);
}

/** Fixed code-point cap for public-form Number fields. */
export const PUBLIC_FORM_NUMBER_MAX_LENGTH = 15;

/** Counts Unicode code points, matching PostgreSQL VARCHAR semantics. */
export function countCodePoints(value) {
	return Array.from(value ?? "").length;
}

/** Parses a configured positive MaxLength and retains a coercion exception for callers that can log it. */
export function parsePositiveMaxLengthResult(rawValue) {
	if (rawValue == null) return { value: undefined };
	try {
		const parsed = Number.parseInt(String(rawValue), 10);
		if (!Number.isFinite(parsed)) return { value: undefined, invalidReason: "non_numeric" };
		if (parsed <= 0) return { value: undefined, invalidReason: "non_positive" };
		return { value: Math.min(parsed, FORM_FIELD_MAX_LENGTH) };
	} catch (error) {
		return { value: undefined, error };
	}
}

/** Parses a configured positive MaxLength and caps it at the database limit. */
export function parsePositiveMaxLength(rawValue) {
	return parsePositiveMaxLengthResult(rawValue).value;
}

/** Formats the API-configured MaxLength error template. */
export function formatMaxLengthErrorMessage(errorTemplate, fieldName, maxLength) {
	if (typeof errorTemplate === "string" && errorTemplate.length > 0) {
		// API-generated default literal templates captured the title that existed when the validator was created.
		// Limit the compatibility repair to that default contract so configured literal messages remain unchanged.
		const generatedSuffix = ` must not exceed ${maxLength} characters.`;
		if (maxLength === PUBLIC_FORM_DEFAULT_MAX_LENGTH && errorTemplate.endsWith(generatedSuffix)) {
			return `${fieldName}${generatedSuffix}`;
		}
		return errorTemplate.replace(/\{(name|value)\}/g, (_match, key) => (key === "name" ? fieldName : String(maxLength)));
	}
	return `${fieldName} must not exceed ${maxLength} characters.`;
}

/**
 * Maps Google address components (Places New `longText`/`shortText`/`types`) onto the structured parts
 * every address consumer uses. Shared by contact-api (proxy normalisation) and contact-admin.
 */
export function parseAddressComponents(components) {
	const parts = { streetNumber: null, street: null, city: null, state: null, postalCode: null, country: null };
	if (!components || components.length === 0) return parts;
	for (const component of components) {
		const types = component.types ?? [];
		const longText = component.longText || component.shortText || null;
		if (types.includes("street_number")) {
			parts.streetNumber = longText;
		} else if (types.includes("route")) {
			parts.street = longText;
		} else if (types.includes("locality")) {
			parts.city = longText;
		} else if (!parts.city && types.includes("postal_town")) {
			parts.city = longText;
		} else if (types.includes("administrative_area_level_1")) {
			parts.state = component.shortText || component.longText || null;
		} else if (types.includes("postal_code")) {
			parts.postalCode = longText;
		} else if (types.includes("country")) {
			parts.country = component.shortText || component.longText || null;
		}
	}
	return parts;
}

function cleanAddressPart(value) {
	return typeof value === "string" ? value.trim() : "";
}

/**
 * One-line address for FormSubmissionFields.value. A Google formattedAddress wins when present;
 * otherwise the manual parts are joined "street, city, state postal, country". The unit is
 * always prepended so it survives in the plain-text value every viewer shows.
 */
export function buildFormattedAddress(details) {
	const unit = cleanAddressPart(details?.unit);
	const formatted = cleanAddressPart(details?.formattedAddress);
	let line = formatted;
	if (!line) {
		const streetLine = [cleanAddressPart(details?.streetNumber), cleanAddressPart(details?.street)].filter(Boolean).join(" ");
		const regionLine = [cleanAddressPart(details?.state), cleanAddressPart(details?.postalCode)].filter(Boolean).join(" ");
		line = [streetLine, cleanAddressPart(details?.city), regionLine, cleanAddressPart(details?.country)].filter(Boolean).join(", ");
	}
	return [unit, line].filter(Boolean).join(", ");
}

export const AUTOMATIC_MESSAGE_TEMPLATE_DEFINITIONS = Object.freeze({
	scheduledReminder: Object.freeze({
		defaultTemplate:
			"You are receiving a message from ${user} at ${organization}. You have a scheduled call on ${day}, ${date}, at ${time} ${timezone}.",
		visiblePlaceholders: Object.freeze(["${user}", "${organization}", "${day}", "${date}", "${time}", "${timezone}", "${scheduledDate}", "${url}"]),
		requiresUrl: true
	}),
	scheduledCancellation: Object.freeze({
		defaultTemplate: "Hi, ${user} from ${organization} has canceled your video call scheduled for ${day}, ${date}, at ${time} ${timezone}.",
		visiblePlaceholders: Object.freeze(["${user}", "${organization}", "${day}", "${date}", "${time}", "${timezone}", "${scheduledDate}"]),
		requiresUrl: false
	}),
	uploadReminder: Object.freeze({
		defaultTemplate: "It looks like you have files that still need to be uploaded for ${organization}. Tap here to finish:",
		visiblePlaceholders: Object.freeze(["${user}", "${organization}", "${url}"]),
		requiresUrl: true
	}),
	uploadConfirmation: Object.freeze({
		defaultTemplate: "Thank you from ${user} at ${organization}. Your content has been received.",
		visiblePlaceholders: Object.freeze(["${user}", "${organization}"]),
		requiresUrl: false
	}),
	// LSP-21829: the reminder a customer gets while a Google review request is still waiting on a rating.
	// `${url}` is that recipient's own rating-page link.
	googleReviewReminder: Object.freeze({
		defaultTemplate: "Just a friendly reminder from ${organization}. We'd love to hear how we did. It only takes a minute:",
		visiblePlaceholders: Object.freeze(["${user}", "${organization}", "${url}"]),
		requiresUrl: true
	})
});

/** LSP-21829: the label for each star value, index 0 = 1 star. Shared by the rating page, admin and the API. */
export const CUSTOMER_REVIEW_RATING_LABELS = Object.freeze(["Not great", "Could be better", "Fine", "Really good", "Perfect"]);

/** The label for a 1-5 star rating, or null for anything that is not one. */
export function customerReviewRatingLabel(rating) {
	return Number.isInteger(rating) && rating >= 1 && rating <= CUSTOMER_REVIEW_RATING_LABELS.length
		? CUSTOMER_REVIEW_RATING_LABELS[rating - 1]
		: null;
}

/** The rating page's counter limit: a soft limit with an amber warning, never a hard cap (CLAUDE.md "Form field length limits"). */
export const CUSTOMER_REVIEW_TEXT_SOFT_LIMIT = 300;

/** The server's hard backstop for review text, in Unicode code points after trimming. */
export const CUSTOMER_REVIEW_TEXT_HARD_LIMIT = 2000;

/** The "Days between reminders" menu for Google review reminders. */
export const GOOGLE_REVIEW_REMINDER_DAYS_BETWEEN_OPTIONS = Object.freeze([1, 2, 3, 5, 7]);

/** The "Maximum reminders" menu for Google review reminders. */
export const GOOGLE_REVIEW_REMINDER_COUNT_OPTIONS = Object.freeze([1, 2, 3]);

const PLACEHOLDER_PATTERN = /\$\{([^}]*)\}/g;
const SCHEDULED_REMINDER_COMPATIBILITY_PLACEHOLDERS = ["${teamMember}", "${day}", "${timezone}", "${scheduledDate}"];
const BASE_COMPATIBILITY_PLACEHOLDERS = ["${teamMember}"];

export function validateAutomaticMessageTemplate(template, kind) {
	if (typeof template !== "string" || template.trim().length === 0) return { valid: false, reason: "empty" };
	const definition = AUTOMATIC_MESSAGE_TEMPLATE_DEFINITIONS[kind];
	if (!definition) return { valid: false, reason: "invalid-kind" };

	const matches = [...template.matchAll(PLACEHOLDER_PATTERN)].map((match) => match[0]);
	const placeholderOpenCount = template.split("${").length - 1;
	if (placeholderOpenCount !== matches.length || matches.some((placeholder) => placeholder === "${}")) {
		return { valid: false, reason: "malformed-placeholder" };
	}
	const compatibilityPlaceholders =
		kind === "scheduledReminder" ? SCHEDULED_REMINDER_COMPATIBILITY_PLACEHOLDERS : BASE_COMPATIBILITY_PLACEHOLDERS;
	const accepted = new Set([...definition.visiblePlaceholders, ...compatibilityPlaceholders]);
	if (matches.some((placeholder) => !accepted.has(placeholder))) return { valid: false, reason: "invalid-placeholder" };
	if (matches.filter((placeholder) => placeholder === "${url}").length > 1) {
		return { valid: false, reason: "duplicate-url" };
	}
	return { valid: true };
}

export function renderAutomaticMessageTemplate(template, values, options) {
	const requiresUrl = options?.requiresUrl === true;
	const url = values?.url == null ? "" : String(values.url);
	if (requiresUrl && url.trim().length === 0) {
		throw new Error("A URL is required to render this automatic message template.");
	}
	let containsUrlPlaceholder = false;
	const rendered = String(template ?? "").replace(PLACEHOLDER_PATTERN, (placeholder, name) => {
		if (name === "url") containsUrlPlaceholder = true;
		if (!Object.prototype.hasOwnProperty.call(values ?? {}, name)) return placeholder;
		return String(values[name] ?? "");
	});
	if (!requiresUrl || containsUrlPlaceholder) return rendered;
	return `${rendered.trimEnd()} ${url}`;
}
